import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import PageMeta from '../../components/base/PageMeta';
import JsonLd from '../../components/base/JsonLd';
import SiteFooter from '../../components/feature/SiteFooter';

const criteria = [
  { icon: 'ri-rocket-line', color: 'from-violet-500 to-fuchsia-500', title: 'Active & Maintained', desc: 'The tool must be live, regularly updated, and supported by an active team or company.' },
  { icon: 'ri-user-heart-line', color: 'from-emerald-500 to-teal-500', title: 'Genuine Use Case', desc: 'It must solve a real problem for a specific audience — not just another generic SaaS product.' },
  { icon: 'ri-price-tag-3-line', color: 'from-orange-400 to-pink-500', title: 'Transparent Pricing', desc: 'Clear, public pricing page. We don\'t review tools with "contact sales only" unless enterprise-only.' },
  { icon: 'ri-global-line', color: 'from-sky-500 to-cyan-500', title: 'English Availability', desc: 'Must have an English-language version or interface since our audience is global.' },
];

const steps = [
  { num: '01', title: 'Submit the Form', desc: 'Fill in the details below — tool name, URL, category, and a brief pitch.' },
  { num: '02', title: 'Editorial Review', desc: 'Our team checks if the tool fits our criteria. You\'ll hear back within 5–7 business days.' },
  { num: '03', title: 'Full Testing', desc: 'If accepted, a writer gets full access and runs it through our scoring rubric.' },
  { num: '04', title: 'Published Review', desc: 'Your tool goes live with an honest, detailed review — no guarantees on score.' },
];

export default function SubmitToolPage() {
  const { i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const [showSignUp, setShowSignUp] = useState(false);
  const [signUpEmail, setSignUpEmail] = useState('');
  const [signUpPassword, setSignUpPassword] = useState('');
  const [signUpDone, setSignUpDone] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);

  const currentLang = i18n.language === 'vi' ? 'VI' : 'EN';

  const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://example.com';

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Submit a Tool for Review – 247digitrend',
    url: `${siteUrl}/submit-tool`,
    description:
      'Submit your digital tool or SaaS product for an honest, hands-on review by the 247digitrend editorial team. No payment required, no score guarantee.',
    isPartOf: { '@id': `${siteUrl}/#website` },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: 'Submit a Tool', item: `${siteUrl}/submit-tool` },
      ],
    },
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) setLangOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    fetch('https://readdy.ai/api/form/d6v42enb245v7h34oj20', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: data.toString(),
    }).then(() => {
      form.reset();
      setCharCount(0);
      setSubmitted(true);
    });
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Inter', sans-serif" }}>
      <PageMeta
        title="Submit a Tool for Review – Get Your Product Featured | 247digitrend"
        description="Submit your SaaS, AI tool, or digital product for a free, honest review by 247digitrend editors. Our team tests every tool across 7+ criteria before publishing an unbiased score."
        canonical={`${siteUrl}/submit-tool`}
        keywords="submit tool review, SaaS product review, AI tool submission, get product reviewed, 247digitrend"
      />
      <JsonLd data={pageSchema} />
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap" rel="stylesheet" />

      {/* ── SIGN UP MODAL ── */}
      {showSignUp && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => { setShowSignUp(false); setSignUpDone(false); }}>
          <div className="relative bg-white rounded-2xl w-full max-w-sm mx-4 p-8" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => { setShowSignUp(false); setSignUpDone(false); }} className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer">
              <i className="ri-close-line text-gray-600 text-base"></i>
            </button>
            {signUpDone ? (
              <div className="text-center py-4">
                <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 rounded-full bg-emerald-100">
                  <i className="ri-check-line text-emerald-600 text-2xl"></i>
                </div>
                <h3 className="text-lg font-black text-gray-900 mb-2">Registration Successful!</h3>
                <p className="text-sm text-gray-500">Welcome to 247compare.</p>
                <button onClick={() => { setShowSignUp(false); setSignUpDone(false); }} className="mt-6 w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-sm font-semibold py-3 rounded-full cursor-pointer hover:opacity-90 transition-opacity whitespace-nowrap">Close</button>
              </div>
            ) : (
              <>
                <div className="text-center mb-6">
                  <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 rounded-xl bg-gradient-to-br from-violet-500 via-fuchsia-500 to-pink-500">
                    <i className="ri-bar-chart-grouped-line text-white text-base"></i>
                  </div>
                  <h3 className="text-xl font-black text-gray-900 mb-1">Create Account</h3>
                  <p className="text-xs text-gray-500">Join 50K+ users of 247compare</p>
                </div>
                <form onSubmit={(e) => { e.preventDefault(); setSignUpDone(true); }} className="space-y-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Email</label>
                    <input type="email" required value={signUpEmail} onChange={(e) => setSignUpEmail(e.target.value)} placeholder="your@email.com" className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl outline-none focus:border-violet-400 transition-colors" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Password</label>
                    <input type="password" required value={signUpPassword} onChange={(e) => setSignUpPassword(e.target.value)} placeholder="At least 8 characters" className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl outline-none focus:border-violet-400 transition-colors" />
                  </div>
                  <button type="submit" className="w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-sm font-semibold py-3 rounded-full cursor-pointer hover:opacity-90 transition-opacity whitespace-nowrap mt-2">Sign Up Now</button>
                </form>
                <p className="text-xs text-center text-gray-400 mt-4">By signing up, you agree to our <a href="#" className="text-violet-600 underline">Terms</a>.</p>
              </>
            )}
          </div>
        </div>
      )}

      {/* HEADER */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-sm shadow-sm' : 'bg-white'}`}>
        <div className="mx-auto px-8 py-2 flex items-center justify-between">
          <a href="/" className="flex items-center cursor-pointer">
            <img src="https://storage.readdy-site.link/project_files/3785ac0c-31ac-4f9e-b2ab-27ad3777db2a/7544398a-500f-41a6-81c3-aa7efbf1c0d2_logo-247.png?v=04135332188f9bc8b00780c66cd5837d" alt="247digitrend" className="h-36 w-auto object-contain" />
          </a>
          <nav className="hidden md:flex items-center gap-8">
            <a href="/" className="text-[19px] font-medium text-gray-700 hover:text-violet-600 transition-colors cursor-pointer">Home</a>
            <a href="/categories" className="text-[19px] font-medium text-gray-700 hover:text-violet-600 transition-colors cursor-pointer">Categories</a>
            <a href="/reviews" className="text-[19px] font-medium text-gray-700 hover:text-violet-600 transition-colors cursor-pointer">Reviews</a>
            <a href="/comparisons" className="text-[19px] font-medium text-gray-700 hover:text-violet-600 transition-colors cursor-pointer">Comparisons</a>
            <a href="/blog" className="text-[19px] font-medium text-gray-700 hover:text-violet-600 transition-colors cursor-pointer">Blog</a>
          </nav>
          <div className="flex items-center gap-3">
            <div className="relative" ref={langRef}>
              <button onClick={() => setLangOpen(!langOpen)} className="flex items-center gap-1.5 bg-gray-100 hover:bg-gray-200 transition-colors rounded-full px-3 py-1.5 cursor-pointer">
                <i className="ri-translate-2 text-xs text-gray-500"></i>
                <span className="text-xs font-semibold text-gray-700">{currentLang}</span>
                <i className={`ri-arrow-down-s-line text-xs text-gray-400 transition-transform ${langOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {langOpen && (
                <div className="absolute right-0 top-full mt-1.5 bg-white rounded-xl border border-gray-100 overflow-hidden w-32" style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}>
                  {(['VI', 'EN'] as const).map((lang) => (
                    <button key={lang} onClick={() => { i18n.changeLanguage(lang.toLowerCase()); setLangOpen(false); }} className={`w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold transition-colors cursor-pointer ${currentLang === lang ? 'bg-violet-50 text-violet-700' : 'text-gray-600 hover:bg-gray-50'}`}>
                      <span>{lang === 'VI' ? '🇻🇳' : '🇺🇸'}</span>
                      <span>{lang === 'VI' ? 'Tiếng Việt' : 'English'}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button onClick={() => setShowSignUp(true)} className="bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-sm font-semibold px-5 py-2 rounded-full whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity">
              Sign Up
            </button>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="pt-44 pb-16" style={{ background: 'linear-gradient(160deg, #faf5ff 0%, #fdf2ff 40%, #fff0fb 100%)' }}>
        <div className="max-w-3xl mx-auto px-6 text-center">
          <span className="inline-flex items-center gap-2 bg-violet-100 text-violet-700 text-sm font-semibold px-4 py-1.5 rounded-full mb-6">
            <i className="ri-tools-line"></i> Submit a Tool
          </span>
          <h1 className="text-5xl font-black text-gray-900 mb-4 leading-tight">
            Want your tool reviewed?
          </h1>
          <p className="text-lg text-gray-600 leading-relaxed">
            We review new tools every week. Submit yours and our editorial team will evaluate it honestly — no payment required, no score guarantee.
          </p>
        </div>
      </section>

      {/* CRITERIA */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-black text-gray-900 mb-2">What We Look For</h2>
            <p className="text-sm text-gray-500">Tools that meet these criteria get prioritized for review</p>
          </div>
          <div className="grid md:grid-cols-4 gap-5 mb-16">
            {criteria.map((c, i) => (
              <div key={i} className="p-5 rounded-2xl border border-gray-100 hover:border-violet-200 transition-all">
                <div className={`w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br ${c.color} mb-3`}>
                  <i className={`${c.icon} text-white text-base`}></i>
                </div>
                <h3 className="text-sm font-bold text-gray-900 mb-1.5">{c.title}</h3>
                <p className="text-xs text-gray-500 leading-relaxed">{c.desc}</p>
              </div>
            ))}
          </div>

          {/* PROCESS */}
          <div className="text-center mb-10">
            <h2 className="text-2xl font-black text-gray-900 mb-2">The Review Process</h2>
            <p className="text-sm text-gray-500">What happens after you submit</p>
          </div>
          <div className="grid md:grid-cols-4 gap-5 mb-16">
            {steps.map((s, i) => (
              <div key={i} className="relative text-center p-5">
                <div className="text-4xl font-black mb-3" style={{ background: 'linear-gradient(90deg, #9333ea, #c026d3)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>{s.num}</div>
                <h3 className="text-sm font-bold text-gray-900 mb-1.5">{s.title}</h3>
                <p className="text-xs text-gray-500 leading-relaxed">{s.desc}</p>
                {i < steps.length - 1 && (
                  <div className="hidden md:block absolute top-8 right-0 w-6 h-6 flex items-center justify-center translate-x-1/2">
                    <i className="ri-arrow-right-line text-gray-300 text-lg"></i>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FORM */}
      <section className="py-16" style={{ background: '#faf5ff' }}>
        <div className="max-w-2xl mx-auto px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-black text-gray-900 mb-2">Submit Your Tool</h2>
            <p className="text-sm text-gray-500">Fill in the details and we&apos;ll take it from there</p>
          </div>

          {submitted ? (
            <div className="flex flex-col items-center justify-center py-16 bg-white rounded-2xl border border-violet-100">
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-violet-100 mb-4">
                <i className="ri-check-line text-violet-600 text-2xl"></i>
              </div>
              <h3 className="text-lg font-black text-gray-900 mb-2">Submission Received!</h3>
              <p className="text-sm text-gray-500 text-center max-w-xs">We&apos;ll review your submission and respond within 5–7 business days.</p>
              <button onClick={() => setSubmitted(false)} className="mt-6 text-violet-600 text-sm font-semibold cursor-pointer hover:underline">Submit another tool</button>
            </div>
          ) : (
            <form
              data-readdy-form
              action="https://readdy.ai/api/form/d6v42enb245v7h34oj20"
              method="POST"
              onSubmit={handleSubmit}
              className="bg-white rounded-2xl p-8 border border-gray-100 space-y-4"
            >
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">Tool Name <span className="text-red-400">*</span></label>
                  <input name="tool_name" type="text" required placeholder="e.g. Notion, Figma, Canva" className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl outline-none focus:border-violet-400 transition-colors" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">Website URL <span className="text-red-400">*</span></label>
                  <input name="website_url" type="url" required placeholder="https://yourtool.com" className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl outline-none focus:border-violet-400 transition-colors" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Category <span className="text-red-400">*</span></label>
                <select name="category" required className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl outline-none focus:border-violet-400 transition-colors bg-white cursor-pointer">
                  <option value="">Select a category...</option>
                  <option value="AI Tools">AI Tools</option>
                  <option value="Website Builders">Website Builders</option>
                  <option value="Email Marketing">Email Marketing</option>
                  <option value="SEO Tools">SEO Tools</option>
                  <option value="Design Tools">Design Tools</option>
                  <option value="Project Management">Project Management</option>
                  <option value="Video Tools">Video Tools</option>
                  <option value="Finance & Fintech">Finance &amp; Fintech</option>
                  <option value="E-commerce">E-commerce</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Brief Description <span className="text-red-400">*</span></label>
                <textarea
                  name="description"
                  required
                  maxLength={500}
                  rows={4}
                  placeholder="What does your tool do? Who is it for? What makes it unique?"
                  onChange={(e) => setCharCount(e.target.value.length)}
                  className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl outline-none focus:border-violet-400 transition-colors resize-none"
                ></textarea>
                <div className={`text-xs mt-1 text-right ${charCount >= 500 ? 'text-red-500 font-semibold' : 'text-gray-400'}`}>{charCount}/500</div>
                {charCount >= 500 && <p className="text-xs text-red-500 mt-1">Description limit reached.</p>}
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">Your Name <span className="text-red-400">*</span></label>
                  <input name="submitter_name" type="text" required placeholder="Your full name" className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl outline-none focus:border-violet-400 transition-colors" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">Your Email <span className="text-red-400">*</span></label>
                  <input name="email" type="email" required placeholder="you@email.com" className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl outline-none focus:border-violet-400 transition-colors" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1.5">Your Role</label>
                <select name="role" className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl outline-none focus:border-violet-400 transition-colors bg-white cursor-pointer">
                  <option value="">Select your role...</option>
                  <option value="Founder / CEO">Founder / CEO</option>
                  <option value="Marketing Manager">Marketing Manager</option>
                  <option value="PR / Outreach">PR / Outreach</option>
                  <option value="Developer">Developer</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <button
                type="submit"
                disabled={charCount >= 500}
                className="w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-sm font-bold py-3.5 rounded-xl whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Submit for Review
              </button>
              <p className="text-xs text-gray-400 text-center">Submitting a tool does not guarantee a review or a positive score.</p>
            </form>
          )}
        </div>
      </section>

      {/* FOOTER */}
      <SiteFooter />
    </div>
  );
}
